import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { isEqual, isFunction } from "lodash";

export interface QueryParams {
  [key: string]: string | number | undefined;
}

interface NotificationsContextProps {
  queryParams: QueryParams;
  setQueryParamsBase: React.Dispatch<React.SetStateAction<QueryParams>>;
  setQueryParams: (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => void;
}

const NotificationsContext = createContext<NotificationsContextProps | undefined>(undefined);

export function useNotificationsContext(): NotificationsContextProps {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotificationsContext must be used within a NotificationsProvider");
  }
  return context;
}

interface NotificationsProviderProps {
  children: ReactNode;
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const history = useHistory();
  const [queryParams, setQueryParamsBase] = useState<QueryParams>({});

  const setQueryParams = useCallback(
    (nextQueryParams: QueryParams | ((prev: QueryParams) => QueryParams)) => {
      setQueryParamsBase(prevQueryParams => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }

        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }

        for (let key in nextQueryParams) {
          if (nextQueryParams.hasOwnProperty(key) && !nextQueryParams[key]) {
            delete nextQueryParams[key];
          }
        }

        const path = history.location.pathname;
        const newQueryParams = new URLSearchParams(nextQueryParams as Record<string, string>);
        history.replace(`${path}?${newQueryParams.toString()}`);

        return nextQueryParams;
      });
    },
    [history]
  );

  const value: NotificationsContextProps = {
    queryParams,
    setQueryParamsBase,
    setQueryParams,
  };

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}